import { useEffect, useRef, useState } from 'react';
import { api } from '../api/client';
import type { AssistPayload, IncidentSummary, SupportPendingPayload } from '../api/types';

/**
 * How many operators are waiting for a person, polled for the whole console —
 * on BOTH queues: a screen share (Assist) and a help conversation (Support).
 *
 * WHY THIS IS NOT ON EITHER SCREEN
 *
 * A request nobody sees is the failure mode of both features. An operator asks
 * while stuck, and a queue is only looked at by somebody who has already
 * decided to look — so the screen that shows waiting requests is exactly the
 * screen nobody is on when one arrives. The counts therefore live in the shell,
 * beside the nav, on every screen.
 *
 * ONE HOOK, NOT ONE PER QUEUE. This hook owns `document.title` outright, and a
 * second copy for the other queue would fight it for the title on every render.
 * One hook, two counts, one title.
 *
 * Slower than either screen's own 4s poll on purpose: this runs everywhere,
 * forever, and its job is "is anyone waiting", not "keep a live queue fresh".
 * The /assist read is the full list on purpose — it is what runs the assist
 * sweep (routes/index.js); /support/pending is a cached read of the CRM.
 *
 * Each queue is polled only for somebody who can open its screen. The server
 * would refuse the read anyway, and a count of requests nobody here can act on
 * is a badge that only nags.
 *
 * AND THE INCIDENTS NOBODY HAS ACKNOWLEDGED, for the same reason: the Now board
 * is only read by somebody already on it. Its count goes on Now's nav item and
 * into the title beside the queues', from /incidents/summary -- the cheap read,
 * not the board.
 */
const POLL_MS = 15_000;

const TITLE = 'sphere-watch';

export function useAssistPending({
  assist: watchAssist,
  support: watchSupport,
  incidents: watchIncidents = false,
}: {
  assist: boolean;
  support: boolean;
  incidents?: boolean;
}) {
  const [pending, setPending] = useState(0);
  const [supportPending, setSupportPending] = useState(0);
  const [supportUrgent, setSupportUrgent] = useState(0);
  const [incidents, setIncidents] = useState<{ open: number; unacknowledged: number; stalled: boolean }>({
    open: 0,
    unacknowledged: 0,
    stalled: false,
  });
  const previous = useRef(0);
  const previousSupport = useRef(0);
  const [lastArrival, setLastArrival] = useState(0);
  const [lastSupportArrival, setLastSupportArrival] = useState(0);

  useEffect(() => {
    if (!watchAssist && !watchSupport && !watchIncidents) return;
    let cancelled = false;

    const tick = async () => {
      // Separately, so one queue's failure never blanks the other's count.
      const [assist, support, board] = await Promise.allSettled([
        watchAssist ? api.get<AssistPayload>('/assist') : Promise.reject(new Error('not watched')),
        watchSupport ? api.get<SupportPendingPayload>('/support/pending') : Promise.reject(new Error('not watched')),
        watchIncidents ? api.get<IncidentSummary>('/incidents/summary') : Promise.reject(new Error('not watched')),
      ]);
      if (cancelled) return;
      // A failed poll is not zero waiting operators. Holding the last known
      // count is the safer error: it may be stale, but it never says "nobody
      // needs you" on the strength of one dropped request.
      if (assist.status === 'fulfilled') {
        const waiting = assist.value.open.filter((session) => session.status === 'pending').length;
        // A rising edge, not a non-zero count: re-announcing the same request
        // every fifteen seconds is how an alert gets ignored.
        if (waiting > previous.current) setLastArrival(Date.now());
        previous.current = waiting;
        setPending(waiting);
      }
      if (support.status === 'fulfilled' && support.value.available) {
        const waiting = support.value.waiting;
        if (waiting > previousSupport.current) setLastSupportArrival(Date.now());
        previousSupport.current = waiting;
        setSupportPending(waiting);
        setSupportUrgent(support.value.urgent);
      }
      // Held on failure for the same reason as the queues: one dropped poll is
      // not "nothing needs anybody".
      if (board.status === 'fulfilled') {
        setIncidents({
          open: board.value.open,
          unacknowledged: board.value.unacknowledged,
          stalled: board.value.monitor.stalled,
        });
      }
    };

    void tick();
    const timer = setInterval(() => void tick(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [watchAssist, watchSupport, watchIncidents]);

  /**
   * The tab title, because the console is usually one of twenty tabs and the
   * badge beside the nav is only visible to somebody already looking at it.
   * Everyone waiting, on either queue — it answers "does anyone need me" —
   * then the incidents nobody has acknowledged, and a monitor that has stopped
   * looking, which outranks both.
   */
  const total = pending + supportPending;
  const { unacknowledged, stalled } = incidents;
  useEffect(() => {
    const parts = [
      stalled ? '⚠ not looking' : '',
      total > 0 ? `(${total})` : '',
      unacknowledged > 0 ? `${unacknowledged} unacked` : '',
    ].filter(Boolean);
    document.title = parts.length ? `${parts.join(' ')} · ${TITLE}` : TITLE;
    return () => {
      document.title = TITLE;
    };
  }, [total, unacknowledged, stalled]);

  return { pending, lastArrival, supportPending, supportUrgent, lastSupportArrival, incidents };
}

/**
 * A desktop notification, which is the only one of the three signals that
 * reaches somebody who is not looking at this tab at all.
 *
 * Permission is NEVER requested on load — a prompt that appears before anyone
 * has expressed interest is the one people deny permanently, and a denied
 * permission cannot be asked for again. The Assist screen has a button.
 */
/**
 * Asks the console to show a screen, from outside its router: a notification's
 * click handler lives in no component. App's OpenRequestedScreens, inside the
 * router, listens and navigates. A click on "Someone needs help" used to focus
 * the window and leave whoever clicked it on whatever screen it was on.
 */
export const SCREEN_REQUEST = 'sphere-watch:open-screen';
const requestScreen = (path: string) => window.dispatchEvent(new CustomEvent(SCREEN_REQUEST, { detail: path }));

/**
 * Whether this page can show a notification itself. Not on a phone or a tablet:
 * iPhone Safari has no Notification at all outside an installed app, and
 * Android Chrome grants the permission and then refuses `new Notification()`
 * ("Illegal constructor" -- only a service worker may notify there). Offering
 * the button anyway asked for a permission that then never notified anyone.
 */
const pageCanNotify = () =>
  typeof Notification !== 'undefined' && !window.matchMedia?.('(hover: none) and (pointer: coarse)').matches;

export function notifyAssist(count: number) {
  if (!pageCanNotify() || Notification.permission !== 'granted') return false;
  try {
    const notification = new Notification('Someone needs help', {
      body: count === 1 ? 'An operator is waiting on the Assist queue.' : `${count} operators are waiting.`,
      tag: 'sphere-watch-assist', // replaces rather than stacks
    });
    notification.onclick = () => {
      window.focus();
      requestScreen('/assist');
      notification.close();
    };
    return true;
  } catch {
    return false;
  }
}

/**
 * The same for a help conversation. Its OWN tag: sharing the assist tag would
 * make a chat request silently replace a waiting screen-share alert, and the
 * person who needed the screen share would never be announced.
 */
export function notifySupport(count: number, urgent: number) {
  if (!pageCanNotify() || Notification.permission !== 'granted') return false;
  try {
    const notification = new Notification(urgent ? 'Urgent: someone wants a person' : 'Someone wants a person', {
      body:
        count === 1
          ? 'An operator is waiting on the Support queue.'
          : `${count} operators are waiting on the Support queue${urgent ? ` (${urgent} urgent)` : ''}.`,
      tag: 'sphere-watch-support', // replaces its own, never an assist alert
    });
    notification.onclick = () => {
      window.focus();
      requestScreen('/support');
      notification.close();
    };
    return true;
  } catch {
    return false;
  }
}

export function assistNotificationState(): NotificationPermission | 'unsupported' {
  if (!pageCanNotify()) return 'unsupported';
  return Notification.permission;
}

export async function requestAssistNotifications(): Promise<NotificationPermission | 'unsupported'> {
  if (!pageCanNotify()) return 'unsupported';
  try {
    return await Notification.requestPermission();
  } catch {
    return Notification.permission;
  }
}
